import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Code2, Save, Send } from "lucide-react";
import { endpoints, type DraftItem, type ValidationIssue } from "@/api/client";
import { FirBozzaFormPanel } from "@/components/FirBozzaFormPanel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/input";
import {
  buildFirBozzaPayload,
  payloadToFirBozzaForm,
  type FirBozzaFormState,
} from "@/lib/firBozzaForm";
import { useOperatoreContext } from "@/lib/operatoreContext";

function DraftStatusBadge({ status }: { status: string }) {
  const v =
    status === "sent"
      ? "success"
      : status === "sent_failed"
        ? "destructive"
        : status === "ready"
          ? "warning"
          : "secondary";
  return <Badge variant={v as never}>{status}</Badge>;
}

export function FirDraftEditor({
  draft,
  onClose,
}: {
  draft: DraftItem;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const ctx = useOperatoreContext();
  const [form, setForm] = useState<FirBozzaFormState | null>(null);
  const [showJson, setShowJson] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [validation, setValidation] = useState<{
    valid: boolean;
    issues: ValidationIssue[];
  } | null>(null);

  useEffect(() => {
    const next = payloadToFirBozzaForm(draft.payload, ctx.numIscrSito);
    setForm(next);
    setJsonText(JSON.stringify(draft.payload, null, 2));
    setValidation(null);
  }, [draft.id, draft.updated_at, ctx.numIscrSito]);

  const save = useMutation({
    mutationFn: async () => {
      if (!form) throw new Error("Form non pronto");
      const payload = buildFirBozzaPayload(form);
      return endpoints.drafts.update(draft.id, {
        payload,
        title: draft.title ?? `FIR ${form.eer}`,
      });
    },
    onSuccess: (updated) => {
      toast.success("Salvato");
      setJsonText(JSON.stringify(updated.payload, null, 2));
      qc.invalidateQueries({ queryKey: ["draft", draft.id] });
      qc.invalidateQueries({ queryKey: ["drafts"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const send = useMutation({
    mutationFn: async () => {
      if (!form) throw new Error("Form non pronto");
      const payload = buildFirBozzaPayload(form);
      await endpoints.drafts.update(draft.id, { payload, title: draft.title ?? `FIR ${form.eer}` });
      return endpoints.drafts.send(draft.id);
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["draft", draft.id] });
      qc.invalidateQueries({ queryKey: ["drafts"] });
      if (r.rentri_status >= 400) {
        const body = r.rentri_body;
        const detail =
          typeof body === "object" && body !== null
            ? String(
                (body as Record<string, unknown>).title ??
                  (body as Record<string, unknown>).detail ??
                  JSON.stringify(body).slice(0, 120),
              )
            : String(body ?? "errore");
        toast.error(`RENTRI ha rifiutato (${r.rentri_status})`, { description: detail });
      } else if (r.rentri_status === 202) {
        toast.success("Accettato da RENTRI (in elaborazione)", {
          description: "Controlla Job RENTRI per l'esito finale.",
        });
      } else {
        toast.success(`Inviato a RENTRI (${r.rentri_status})`);
      }
    },
    onError: (e: Error) => toast.error(`Invio fallito: ${e.message}`),
  });

  const validate = async () => {
    if (!form) return;
    try {
      const payload = buildFirBozzaPayload(form);
      const r = await endpoints.validate.fir(payload);
      setValidation(r);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Validazione fallita");
    }
  };

  if (!form) return null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {draft.title ?? `Bozza FIR ${draft.id.slice(0, 8)}`}
          </h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
            <Badge variant="outline">fir</Badge>
            <DraftStatusBadge status={draft.status} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={onClose}>
            Chiudi
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowJson((v) => !v)}>
            <Code2 className="mr-2 h-4 w-4" /> {showJson ? "Nascondi JSON" : "JSON"}
          </Button>
          <Button variant="outline" onClick={validate}>
            Valida
          </Button>
          <Button variant="outline" onClick={() => save.mutate()} disabled={save.isPending}>
            <Save className="mr-2 h-4 w-4" /> Salva
          </Button>
          <Button
            onClick={() => send.mutate()}
            disabled={draft.status === "sent" || send.isPending}
          >
            <Send className="mr-2 h-4 w-4" /> Invia a RENTRI
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Dati formulario</CardTitle>
        </CardHeader>
        <CardContent>
          <FirBozzaFormPanel form={form} onChange={setForm} />
        </CardContent>
      </Card>

      {validation && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Validazione</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge variant={validation.valid ? "success" : "destructive"} className="mb-2">
              {validation.valid ? "Valido" : "Non valido"}
            </Badge>
            {validation.issues.length === 0 && (
              <p className="text-sm text-muted-foreground">Nessun problema rilevato.</p>
            )}
            <ul className="space-y-1 text-sm">
              {validation.issues.map((i, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Badge variant={i.severity === "error" ? "destructive" : "warning"}>
                    {i.severity}
                  </Badge>
                  <span className="font-mono text-xs text-muted-foreground">{i.field}</span>
                  <span>{i.message}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {showJson && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Payload (JSON)</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              className="min-h-[240px] font-mono text-xs"
              value={jsonText}
              readOnly
              spellCheck={false}
            />
          </CardContent>
        </Card>
      )}

      {draft.rentri_response != null && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Risposta RENTRI</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="overflow-x-auto rounded bg-muted/50 p-3 text-xs">
              {JSON.stringify(draft.rentri_response, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
